import { createFileRoute } from "@tanstack/react-router";
import { Database, Search, UserPlus } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/ai-enrichment-hero.webp";
import cardImg from "@/assets/ai-enrichment-card.webp";
import workflowsImg from "@/assets/ai-enrichment-workflows.webp";

const IMG: Record<string, string> = { hero: heroImg, card: cardImg, workflows: workflowsImg };

export const Route = createFileRoute("/vi/platform/ai-enrichment")({
  head: () => ({
    meta: [
      { title: "Làm giàu liên hệ bằng AI — banana.cards" },
      { name: "description", content: "Tự điền mọi liên hệ với chức danh, công ty, social và signals — từ một email, quét hoặc link LinkedIn." },
      { property: "og:title", content: "Làm giàu liên hệ bằng AI — banana.cards" },
      { property: "og:description", content: "Tự điền mọi liên hệ với chức danh, công ty, social và signals." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/ai-enrichment", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Làm giàu AI"
      title="Mọi liên hệ,"
      italic="tức thì hoàn chỉnh."
      description="Thả một email, link LinkedIn hoặc thẻ giấy đã quét. Banana làm giàu với chức danh, công ty, social, vị trí và signals — CRM của bạn không bao giờ trống nữa."
      heroImage={IMG['hero']}
      metrics={[{ v: "85%", l: "trường tự điền" }, { v: "3 giây", l: "mỗi liên hệ" }, { v: "60+", l: "nguồn dữ liệu" }]}
      bullets={[
        { icon: UserPlus, title: "Tự xây liên hệ", desc: "Từ một thẻ, email hay chỉ một cái tên — Banana điền phần còn lại." },
        { icon: Search, title: "Dữ liệu xác thực", desc: "Đối chiếu từ 60+ nguồn với điểm tin cậy cho mỗi trường." },
        { icon: Database, title: "Luôn mới", desc: "Làm mới theo lịch để chức danh và công ty luôn cập nhật." }
      ]}
      deepDives={[
        { eyebrow: "Nguồn", title: "Dữ liệu công khai,", italic: "độ chính xác riêng tư.", description: "Chúng tôi kết hợp signals email, hồ sơ công khai và company graph để điền chức danh, cấp bậc, vị trí, tech stack và hơn — với provenance đi kèm.", image: IMG["card"], reverse: false, points: ["Chức danh, cấp bậc và phòng ban", "Quy mô, ngành và HQ công ty", "Email công việc & domain xác thực", "Hồ sơ social và website cá nhân"] },
        { eyebrow: "Workflow", title: "Làm giàu khi capture,", italic: "hoặc hàng loạt.", description: "Làm giàu mọi lead mới tự động, hoặc import CSV và xử lý cả lô trong vài phút.", image: IMG["workflows"], reverse: true, points: ["Làm giàu khi quét/submit form", "Làm giàu CSV hàng loạt", "Refresh theo lịch cho mỗi segment", "Webhook khi trường thay đổi"] }
      ]}
      howItWorks={[
        { title: "Capture lead", desc: "Quét, form, import hoặc dán email." },
        { title: "Banana làm giàu", desc: "Chức danh, công ty, social và signals." },
        { title: "Hiện độ tin cậy", desc: "Thấy provenance mỗi trường." },
        { title: "Refresh theo lịch", desc: "Giữ chính xác khi người ta thay đổi." }
      ]}
      capabilities={["Email → hồ sơ", "Làm giàu từ LinkedIn URL", "OCR thẻ giấy", "Firmographics công ty", "Phát hiện tech stack", "Tin cậy mỗi trường", "Re-enrich theo lịch", "Provenance mỗi trường"]}
      faq={[
        { q: "Làm giàu AI điền những trường gì?", a: "Từ một email hoặc LinkedIn: họ tên đầy đủ, chức danh, cấp bậc, công ty, domain, quy mô và ngành, vị trí, ảnh hồ sơ, social công khai, timezone — kèm điểm tin cậy." },
        { q: "Dữ liệu đến từ đâu?", a: "Dữ liệu web công khai, đối tác API và signals từ inbox/lịch của bạn (khi kết nối). Không bao giờ lấy từ CRM của khách khác." },
        { q: "Có tuân thủ GDPR và CCPA không?", a: "Có — provenance theo trường, người dùng có thể yêu cầu xóa, và bạn có thể tắt làm giàu theo khu vực." },
        { q: "Độ chính xác bao nhiêu?", a: "Mỗi trường có điểm tin cậy 0–100. Cao (>85) tự điền; trung bình (60–85) gắn flag 'review'; thấp ẩn mặc định. ~90%+ cho tên công ty và chức danh B2B." }
      ]}
      outro={{ title: "Không bao giờ dán", italic: "vào CRM nữa.", description: "Capture một lần, Banana điền phần còn lại — và giữ luôn mới." }}
    />
  );
}